import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Databases, Query, ID, Account } from 'appwrite';
import client from '../app-user';
import { AuthContext } from '../auth-context';
import LoadingSpinner from '../components/LoadingSpinner';
import freeAgentPlayerMetadata from '../data/free-agent-player-metadata.json';
import styles from '../styles/free-agency.module.css';
import { calculateBidValue, compareFreeAgentBids } from '../utils/freeAgencyBids';

// Helper to fallback on default env values
function getEnvVariable(name: string, defaultValue: string) {
  const value = process.env[name];
  if (value === undefined) {
    console.warn(`Environment variable ${name} is not set. Using default: ${defaultValue}`);
    return defaultValue;
  }
  return value;
}

// Appwrite config
const DATABASE_ID = getEnvVariable('REACT_APP_DATABASE_ID', 'database');
const FREE_AGENTS_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_FREE_AGENTS_DATA_COLLECTION_ID', 'free-agents-data');
const FREE_AGENTS_BIDS_COLLECTION_ID = getEnvVariable('REACT_APP_FREE_AGENTS_BIDS_COLLECTION_ID', 'free-agents-bids');
const ROSTERED_PLAYER_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_ROSTERED_PLAYER_DATA_COLLECTION_ID', 'roster data');
const DEAD_CAP_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_DEAD_CAP_DATA_COLLECTION_ID', 'dead cap');
const TEAMS_COLLECTION_ID = getEnvVariable('REACT_APP_TEAMS_COLLECTION_ID', 'teams');
const SALARY_CAP = 188;
const PACIFIC_TIME_ZONE = 'America/Los_Angeles';
const HOURS_TO_MILLISECONDS = 60 * 60 * 1000;

const databases = new Databases(client);
const account = new Account(client);

// Types
interface FreeAgentDoc {
  playerId: string;
  name: string;
  position: string;
  team?: string | null;
  fantasy_positions?: string[] | null;
}

interface PlayerMetadata {
  team: string;
  positions: string[];
}

interface BidDoc {
  playerId: string;
  playerName: string;
  playerPosition: string;
  bidAmount: number;
  bidDuration: number;
  owner: string;
  $createdAt: string; // Appwrite timestamp
  // add other fields if needed
}

interface TeamDoc {
  name: string;
  sleeperID?: string;
  UserAuthId?: string;
  userAuthId?: string;
}

interface RosteredPlayerDoc {
  roster_id: string;
  owner: string;
  team_name?: string;
  salary: number;
}

interface DeadCapDoc {
  roster_id: string;
  salary: number;
}

interface AgentWithBid extends FreeAgentDoc {
  currentTeam: string;
  displayPosition: string;
  fantasyPositions: string[];
  highestBid: number;
  highestBidOwner?: string;
  highestBidDuration?: number;
  lastBidTime?: string;
  highlightClass: string;
  canBid: boolean;
  hasUserBid: boolean;
}

const PLAYER_METADATA = freeAgentPlayerMetadata as Record<string, PlayerMetadata>;

type PlayerFilter = 'QB' | 'RB' | 'WR' | 'TE' | 'DL' | 'LB' | 'DB' | 'K' | 'my-bids';

const FILTER_OPTIONS: Array<{ label: string; value: PlayerFilter }> = [
  { label: 'QB', value: 'QB' },
  { label: 'RB', value: 'RB' },
  { label: 'WR', value: 'WR' },
  { label: 'TE', value: 'TE' },
  { label: 'DL', value: 'DL' },
  { label: 'LB', value: 'LB' },
  { label: 'DB', value: 'DB' },
  { label: 'K', value: 'K' },
  { label: 'My bids', value: 'my-bids' },
];

const normalizeValue = (value?: unknown) => String(value ?? '').trim().toLowerCase();

const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(value);

const fetchAllDocuments = async <T,>(collectionId: string): Promise<T[]> => {
  const documents: T[] = [];
  const limit = 100;
  let offset = 0;

  while (true) {
    const result = await databases.listDocuments(
      DATABASE_ID,
      collectionId,
      [Query.limit(limit), Query.offset(offset)]
    );
    documents.push(...(result.documents as unknown as T[]));
    if (result.documents.length < limit) break;
    offset += limit;
  }

  return documents;
};

const getTimeZoneDateParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  return Object.fromEntries(
    parts
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, Number(part.value)])
  ) as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>;
};

const getTimeZoneOffset = (date: Date, timeZone: string) => {
  const parts = getTimeZoneDateParts(date, timeZone);
  const dateWithoutMilliseconds = Math.floor(date.getTime() / 1000) * 1000;
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  ) - dateWithoutMilliseconds;
};

const getNextPacificMidnight = (date: Date) => {
  const pacificDate = getTimeZoneDateParts(date, PACIFIC_TIME_ZONE);
  const nextCalendarDay = new Date(Date.UTC(pacificDate.year, pacificDate.month - 1, pacificDate.day + 1));
  const utcMidnight = Date.UTC(
    nextCalendarDay.getUTCFullYear(),
    nextCalendarDay.getUTCMonth(),
    nextCalendarDay.getUTCDate()
  );

  let targetTime = utcMidnight;
  for (let iteration = 0; iteration < 2; iteration += 1) {
    targetTime = utcMidnight - getTimeZoneOffset(new Date(targetTime), PACIFIC_TIME_ZONE);
  }

  return new Date(targetTime);
};

const getHoursUntilPacificMidnight = (date: Date) =>
  Math.max(0, (getNextPacificMidnight(date).getTime() - date.getTime()) / HOURS_TO_MILLISECONDS);

// Time-based highlighting logic using actual Pacific midnights, including DST changes.
type Highlight = { highlightClass: string; canBid: boolean };

const getHighlightClass = (
  lastBidTime?: string
): Highlight => {
  // No bids ever placed
  if (!lastBidTime) {
    return { highlightClass: '', canBid: true };
  }

  const now = new Date();
  const bidTime = new Date(lastBidTime);
  const MS_IN_12_HOURS = 12 * 60 * 60 * 1000;

  // 1) Compute the next two Pacific midnights after the bid.
  const firstThresh = getNextPacificMidnight(bidTime);
  const secondThresh = getNextPacificMidnight(firstThresh);

  // 3) Green: still before the first bid-relative threshold
  if (now < firstThresh) {
    return { highlightClass: styles.greenHighlight, canBid: true };
  }

  // 4) Red: past the second threshold → bid is stale
  if (now >= secondThresh) {
    return { highlightClass: styles.redHighlight, canBid: false };
  }

  // 5) Yellow: between first and second threshold *and* within 12h of second
  const msToRed = secondThresh.getTime() - now.getTime();
  if (msToRed <= MS_IN_12_HOURS) {
    return { highlightClass: styles.yellowHighlight, canBid: true };
  }

  // 6) Between thresholds but more than 12h to go → no highlight
  return { highlightClass: '', canBid: true };
};

const FreeAgencyPage: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [agents, setAgents] = useState<AgentWithBid[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedAgent, setSelectedAgent] = useState<AgentWithBid | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [bidDuration, setBidDuration] = useState<number>(1);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showLightbox, setShowLightbox] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<PlayerFilter | null>(null);
  const [showSignedPlayers, setShowSignedPlayers] = useState<boolean>(false);
  const [capSpaceRemaining, setCapSpaceRemaining] = useState<number | null>(null);
  const [hoursUntilPacificMidnight, setHoursUntilPacificMidnight] = useState(() => getHoursUntilPacificMidnight(new Date()));

  // Fetch free agents and their bids, compute highest and highlighting
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [freeAgentsRaw, bidsRaw] = await Promise.all([
        fetchAllDocuments<FreeAgentDoc>(FREE_AGENTS_DATA_COLLECTION_ID),
        fetchAllDocuments<BidDoc>(FREE_AGENTS_BIDS_COLLECTION_ID),
      ]);

      const userOwnerAliases = new Set([
        normalizeValue(user.session?.name),
        normalizeValue(user.session?.email),
      ].filter(Boolean));
      const userBidPlayerIds = new Set(
        bidsRaw
          .filter(bid => userOwnerAliases.has(normalizeValue(bid.owner)))
          .map(bid => bid.playerId)
      );

      if (user.session) {
        try {
          const [teams, rosteredPlayers, deadCap] = await Promise.all([
            fetchAllDocuments<TeamDoc>(TEAMS_COLLECTION_ID),
            fetchAllDocuments<RosteredPlayerDoc>(ROSTERED_PLAYER_DATA_COLLECTION_ID),
            fetchAllDocuments<DeadCapDoc>(DEAD_CAP_DATA_COLLECTION_ID),
          ]);

          const normalizedSessionId = normalizeValue(user.session.id);
          const normalizedUserName = normalizeValue(user.session.name);
          const userTeam = teams.find(team => {
            const authId = normalizeValue(team.UserAuthId || team.userAuthId);
            return authId && authId === normalizedSessionId;
          }) || teams.find(team => normalizeValue(team.name) === normalizedUserName);

          const teamIdentifiers = new Set([
            normalizeValue(userTeam?.sleeperID),
            normalizeValue(userTeam?.name),
            normalizedUserName,
          ].filter(Boolean));
          const teamRoster = rosteredPlayers.filter(player =>
            teamIdentifiers.has(normalizeValue(player.owner)) ||
            teamIdentifiers.has(normalizeValue(player.team_name)) ||
            teamIdentifiers.has(normalizeValue(player.roster_id))
          );
          const rosterIds = new Set(teamRoster.map(player => normalizeValue(player.roster_id)).filter(Boolean));
          const teamDeadCap = deadCap.filter(player => rosterIds.has(normalizeValue(player.roster_id)));
          const committedSalary = [...teamRoster, ...teamDeadCap]
            .reduce((total, player) => total + (Number(player.salary) || 0), 0);

          setCapSpaceRemaining(userTeam || teamRoster.length > 0 ? SALARY_CAP - committedSalary : null);
        } catch (capError) {
          console.error('Error fetching cap space:', capError);
          setCapSpaceRemaining(null);
        }
      } else {
        setCapSpaceRemaining(null);
      }

      // Group bids by playerId, keep highest and most recent
      type BidWithValue = BidDoc & { value: number };

      const playerBidsMap: Record<
        string,
        { highest: BidWithValue; mostRecent: BidDoc }
      > = {};
      bidsRaw.forEach(bid => {
        const value = calculateBidValue(bid.bidAmount, bid.bidDuration);
        const bidWithValue: BidWithValue = { ...bid, value };

        const current = playerBidsMap[bid.playerId];
        if (!current) {
          // first time seeing this player
          playerBidsMap[bid.playerId] = { highest: bidWithValue, mostRecent: bid };
        } else {
          // At $0, a longer contract wins; otherwise rank by adjusted value.
          if (compareFreeAgentBids(bidWithValue, current.highest) < 0) {
            current.highest = bidWithValue;
          }
          // and still track “most recent” purely by timestamp
          if (new Date(bid.$createdAt) > new Date(current.mostRecent.$createdAt)) {
            current.mostRecent = bid;
          }
        }
      });

      // Merge free agents with bid info and highlighting
      const merged: AgentWithBid[] = freeAgentsRaw.map(agent => {
        const bidInfo = playerBidsMap[agent.playerId];
        const lastBidTime = bidInfo?.mostRecent.$createdAt;
        const { highlightClass, canBid } = getHighlightClass(lastBidTime);
        const playerMetadata = PLAYER_METADATA[agent.playerId];
        const fantasyPositions = Array.from(new Set(
          (agent.fantasy_positions?.length ? agent.fantasy_positions : playerMetadata?.positions || [agent.position])
            .filter(Boolean)
        ));

        return {
          ...agent,
          currentTeam: agent.team || playerMetadata?.team || 'FA',
          displayPosition: fantasyPositions.join('/') || agent.position,
          fantasyPositions,
          // raw amount & duration
          highestBid: bidInfo?.highest.bidAmount || 0,
          highestBidDuration: bidInfo?.highest.bidDuration,
          highestBidOwner: bidInfo?.highest.owner || '',
          // new: effective value after duration multiplier
          highestBidValue: bidInfo?.highest.value || 0,
          lastBidTime,
          highlightClass,
          canBid,
          hasUserBid: userBidPlayerIds.has(agent.playerId),
        };
      });

      // 1) define your custom order
      const positionOrder: Record<string, number> = {
        QB: 0,
        RB: 1,
        WR: 2,
        TE: 3,
        DL: 4,
        LB: 5,
        DB: 6,
        K: 7,
      };

      // 2) sort merged array in place
      merged.sort((a, b) => {
        // first: signed players (canBid === false) should go last
        if (a.canBid !== b.canBid) {
          return a.canBid ? -1 : 1;
        }
        // then: both are either signed or unsigned → sort by position
        const aPos = positionOrder[a.position] ?? Infinity;
        const bPos = positionOrder[b.position] ?? Infinity;
        return aPos - bPos;
      });

      // 3) now set state
      setAgents(merged);
    } catch (err) {
      console.error('Error fetching free agency data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user.session]);

  useEffect(() => {
    fetchData();
    // Refresh data every minute to update highlighting
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const updateCountdown = () => setHoursUntilPacificMidnight(getHoursUntilPacificMidnight(new Date()));
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 60000);
    return () => window.clearInterval(interval);
  }, []);

  const visibleAgents = useMemo(() => agents.filter(agent => {
    if (!showSignedPlayers && !agent.canBid) return false;
    if (!activeFilter) return true;
    if (activeFilter === 'my-bids') return agent.hasUserBid;
    return agent.fantasyPositions.includes(activeFilter);
  }), [activeFilter, agents, showSignedPlayers]);

  // Handle opening lightbox for bidding
  const handlePlayerClick = (agent: AgentWithBid) => {
    if (!agent.canBid) {
      alert('Bidding period for this player has ended.');
      return;
    }
    setSelectedAgent(agent);
    setBidAmount((agent.highestBid || 0) + 1);
    setBidDuration(1);
    setShowLightbox(true);
  };

  // Handle bid form submission
  const handleBidSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.session) {
      alert('Please log in to place a bid.');
      return;
    }
    if (!selectedAgent) return;

    setSubmitting(true);
    try {
      const userDetails = await account.get();
      const bidDoc = {
        playerId: selectedAgent.playerId,
        playerName: selectedAgent.name,
        playerPosition: selectedAgent.position,
        bidAmount,
        bidDuration: bidAmount === 0 ? Math.min(bidDuration, 3) : bidDuration,
        owner: userDetails.name || userDetails.email,
      };
      await databases.createDocument(
        DATABASE_ID,
        FREE_AGENTS_BIDS_COLLECTION_ID,
        ID.unique(),
        bidDoc
      );
      // Refresh data to get the new highest bid
      await fetchData();
      setShowLightbox(false);
      setSelectedAgent(null);
    } catch (err) {
      console.error('Error submitting free agency bid:', err);
      alert('Error submitting bid. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Close lightbox
  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedAgent(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h1>Free Agency</h1>
        
        <div className={styles.summaryBar}>
          <div className={styles.capSpace}>
            <span className={styles.capSpaceLabel}>Cap space remaining</span>
            <strong>{capSpaceRemaining === null ? '—' : formatCurrency(capSpaceRemaining)}</strong>
          </div>

          {/* Legend for highlighting */}
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <div className={`${styles.legendColor} ${styles.greenHighlight}`}></div>
              <span>Recent bids</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.legendColor} ${styles.yellowHighlight}`}></div>
              <span>Signing in {hoursUntilPacificMidnight.toFixed(1)} hours</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.legendColor} ${styles.redHighlight}`}></div>
              <span>Player signed</span>
            </div>
          </div>
        </div>

        <div className={styles.filterBar}>
          <div className={styles.filterScroller} role="group" aria-label="Filter free agents">
            {FILTER_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={`${styles.filterPill} ${activeFilter === option.value ? styles.activeFilterPill : ''}`}
                aria-pressed={activeFilter === option.value}
                onClick={() => setActiveFilter(current => current === option.value ? null : option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={showSignedPlayers}
            className={styles.signedToggle}
            onClick={() => setShowSignedPlayers(current => !current)}
          >
            <span className={`${styles.toggleTrack} ${showSignedPlayers ? styles.toggleTrackActive : ''}`} aria-hidden="true">
              <span className={styles.toggleThumb}></span>
            </span>
            <span>Show signed players</span>
          </button>
        </div>

        {/* Player list */}
        <ul className={styles.playerList}>
          {visibleAgents.map(agent => (
            <li
              key={agent.playerId}
              className={`${styles.playerItem} ${agent.highlightClass} ${!agent.canBid ? styles.disabled : ''}`}
              onClick={() => handlePlayerClick(agent)}
            >
              <img
                src={`https://sleepercdn.com/content/nfl/players/thumb/${agent.playerId}.jpg`}
                alt={agent.name}
                className={styles.playerPic}
              />
              <div className={styles.info}>
                <span className={styles.name}>{agent.name}</span>
                <span className={styles.position}>{agent.displayPosition} - {agent.currentTeam}</span>
              </div>
              <div className={styles.bidInfo}>
                {agent.lastBidTime ? (
                  <div className={styles.bidAmount}>${agent.highestBid} for {agent.highestBidDuration} year{agent.highestBidDuration !== 1 ? 's' : ''}</div>
                ) : (
                  <div className={styles.emptyBid}>-</div>
                )}
                {agent.highestBidOwner && (
                  <div className={styles.bidOwner}>by {agent.highestBidOwner}</div>
                )}
                {!agent.canBid && (
                  <div className={styles.closedText}>CLOSED</div>
                )}
              </div>
            </li>
          ))}
        </ul>
        {visibleAgents.length === 0 && (
          <p className={styles.emptyState}>No players match these filters.</p>
        )}

        {/* Lightbox for bidding */}
        {showLightbox && selectedAgent && (
          <div className={styles.lightboxOverlay} onClick={closeLightbox}>
            <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.closeButton} onClick={closeLightbox}>×</button>
              
              <div className={styles.playerHeader}>
                <img
                  src={`https://sleepercdn.com/content/nfl/players/thumb/${selectedAgent.playerId}.jpg`}
                  alt={selectedAgent.name}
                  className={styles.lightboxPlayerPic}
                />
                <div>
                  <h2>{selectedAgent.name}</h2>
                  <p className={styles.position}>{selectedAgent.displayPosition} - {selectedAgent.currentTeam}</p>
                </div>
              </div>

              <div className={styles.currentBidInfo}>
                <p>Current Highest Bid: {selectedAgent.lastBidTime ? <strong>${selectedAgent.highestBid}</strong> : <strong className={styles.emptyBid}>-</strong>}</p>
                {selectedAgent.highestBidOwner && (
                  <p>by {selectedAgent.highestBidOwner} for {selectedAgent.highestBidDuration} year{selectedAgent.highestBidDuration !== 1 ? 's' : ''}</p>
                )}
              </div>

              <form onSubmit={handleBidSubmit} className={styles.bidForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="bidAmount">Bid Amount ($)</label>
                  <input
                    id="bidAmount"
                    type="number"
                    value={bidAmount}
                    onChange={e => {
                      const nextAmount = parseFloat(e.target.value);
                      setBidAmount(nextAmount);
                      if (nextAmount === 0) {
                        setBidDuration(currentDuration => Math.min(currentDuration, 3));
                      }
                    }}
                    min={0}
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="bidDuration">Contract Duration (Years)</label>
                  <select
                    id="bidDuration"
                    value={bidDuration}
                    onChange={e => setBidDuration(parseInt(e.target.value, 10))}
                    required
                  >
                    <option value={1}>1 Year</option>
                    <option value={2}>2 Years</option>
                    <option value={3}>3 Years</option>
                    {bidAmount !== 0 && <option value={4}>4 Years</option>}
                    {bidAmount !== 0 && <option value={5}>5 Years</option>}
                  </select>
                </div>
                
                <div className={styles.formActions}>
                  <button type="button" onClick={closeLightbox} className={styles.cancelButton}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className={styles.submitButton}>
                    {submitting ? 'Bidding...' : 'Place Bid'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeAgencyPage;

